import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { quoteService } from '../../../services/quoteService';
import { serviceService } from '../../../services/serviceService';
import { leadService } from '../../../services/leadService';

const QuoteManagementPanel = () => {
  const [quotes, setQuotes] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Form state for new quote
  const [formData, setFormData] = useState({
    serviceId: '',
    clientId: '',
    title: '',
    description: '',
    criteria: {
      surface: '',
      duration: '',
      complexity: 'medium',
    },
  });

  useEffect(() => {
    loadData();

    const unsubscribe = quoteService?.subscribeToChanges((payload) => {
      // Real-time payloads are snake_case — just reload to get proper joins
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
        loadData();
      }
    });

    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [quotesData, servicesData, clientsData] = await Promise.all([
        quoteService?.getAll(),
        serviceService?.getAll(),
        leadService?.getAll(),
      ]);
      setQuotes(quotesData);
      setServices(servicesData);
      setClients(clientsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e?.preventDefault();

    try {
      setLoading(true);
      const selectedService = services?.find((s) => s?.id === formData?.serviceId);

      // Calculate dynamic pricing
      const pricing = quoteService?.calculateDynamicPrice(selectedService, formData?.criteria);

      const newQuote = {
        serviceId: formData?.serviceId,
        clientId: formData?.clientId,
        title: formData?.title,
        description: formData?.description,
        criteria: formData?.criteria,
        basePrice: pricing?.basePrice,
        adjustments: pricing?.adjustments,
        subtotal: pricing?.subtotal,
        totalAmount: pricing?.totalAmount,
        status: 'draft',
        lineItems: [
          {
            description: selectedService?.title,
            quantity: 1,
            unitPrice: pricing?.totalAmount,
            total: pricing?.totalAmount,
          },
        ],
      };

      await quoteService?.create(newQuote);
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await quoteService?.updateStatus(quoteId, newStatus);
      loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;
    try {
      await quoteService?.delete(quoteId);
      loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const resetForm = () => {
    setFormData({
      serviceId: '',
      clientId: '',
      title: '',
      description: '',
      criteria: {
        surface: '',
        duration: '',
        complexity: 'medium',
      },
    });
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les devis' },
    { value: 'draft', label: 'Brouillons' },
    { value: 'sent', label: 'Envoyés' },
    { value: 'accepted', label: 'Acceptés' },
    { value: 'invoiced', label: 'Facturés' },
    { value: 'rejected', label: 'Rejetés' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      draft: 'var(--color-muted-foreground)',
      sent: 'var(--color-trust)',
      accepted: 'var(--color-success)',
      invoiced: 'var(--color-primary)',
      rejected: 'var(--color-error)',
      expired: 'var(--color-warning)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: 'FileText',
      sent: 'Send',
      accepted: 'CheckCircle',
      invoiced: 'DollarSign',
      rejected: 'XCircle',
      expired: 'Clock',
    };
    return icons?.[status] || 'FileText';
  };

  const filteredQuotes =
    selectedStatus === 'all' ? quotes : quotes?.filter((q) => q?.status === selectedStatus);

  if (loading && quotes?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-muted-foreground">Chargement des devis...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-premium border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">
              Gestion des Devis
            </h3>
            <p className="text-sm text-muted-foreground">
              Créez et gérez les devis avec calcul automatique
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
              className="w-40"
            />
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              Nouveau Devis
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-error/10 border-b border-error/20">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      <div className="p-4 md:p-6">
        <div className="space-y-4">
          {filteredQuotes?.map((quote) => (
            <div
              key={quote?.id}
              className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${getStatusColor(quote?.status)}15` }}
                  >
                    <Icon
                      name={getStatusIcon(quote?.status)}
                      size={24}
                      style={{ color: getStatusColor(quote?.status) }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                      {quote?.title}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Icon name="Hash" size={12} />
                        {quote?.quoteNumber}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        {quote?.leads?.name || 'Client'}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        {new Date(quote?.createdAt)?.toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(quote?.status)}15`,
                          color: getStatusColor(quote?.status),
                        }}
                      >
                        {quote?.status?.toUpperCase()}
                      </span>
                      {quote?.services?.title && (
                        <span className="px-2 py-1 bg-background rounded text-xs font-medium text-foreground">
                          {quote?.services?.title}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-3 lg:gap-4">
                  <div className="text-left md:text-right">
                    <p className="text-lg font-bold text-foreground">
                      ${quote?.totalAmount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">Montant Total</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {quote?.status === 'draft' && (
                      <button
                        onClick={() => handleStatusChange(quote?.id, 'sent')}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-trust/10 flex items-center justify-center transition-colors"
                        title="Envoyer"
                      >
                        <Icon name="Send" size={16} className="text-trust" />
                      </button>
                    )}
                    {quote?.status === 'sent' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(quote?.id, 'accepted')}
                          className="w-9 h-9 rounded-lg bg-background hover:bg-success/10 flex items-center justify-center transition-colors"
                          title="Accepter"
                        >
                          <Icon name="CheckCircle" size={16} className="text-success" />
                        </button>
                        <button
                          onClick={() => handleStatusChange(quote?.id, 'rejected')}
                          className="w-9 h-9 rounded-lg bg-background hover:bg-error/10 flex items-center justify-center transition-colors"
                          title="Rejeter"
                        >
                          <Icon name="XCircle" size={16} className="text-error" />
                        </button>
                      </>
                    )}
                    {quote?.status === 'accepted' && (
                      <button
                        onClick={() => handleStatusChange(quote?.id, 'invoiced')}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                        title="Facturer"
                      >
                        <Icon name="DollarSign" size={16} className="text-primary" />
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedQuote(quote)}
                      className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                      title="Détails"
                    >
                      <Icon name="Eye" size={16} className="text-primary" />
                    </button>
                    <button
                      onClick={() => handleDeleteQuote(quote?.id)}
                      className="w-9 h-9 rounded-lg bg-background hover:bg-error/10 flex items-center justify-center transition-colors"
                      title="Supprimer"
                    >
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredQuotes?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun devis trouvé</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Quote Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">Nouveau Devis</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateQuote} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Service *
                </label>
                <select
                  value={formData?.serviceId}
                  onChange={(e) => setFormData({ ...formData, serviceId: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                  required
                >
                  <option value="">Sélectionnez un service</option>
                  {services?.map((service) => (
                    <option key={service?.id} value={service?.id}>
                      {service?.title} - ${service?.basePrice}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Client *
                </label>
                <select
                  value={formData?.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                  required
                >
                  <option value="">Sélectionnez un client</option>
                  {clients?.map((client) => (
                    <option key={client?.id} value={client?.id}>
                      {client?.name} - {client?.email}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="Titre du devis *"
                value={formData?.title}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Surface (m²)"
                  type="number"
                  value={formData?.criteria?.surface}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      criteria: { ...formData?.criteria, surface: e?.target?.value },
                    })
                  }
                />

                <Input
                  label="Durée (mois)"
                  type="number"
                  value={formData?.criteria?.duration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      criteria: { ...formData?.criteria, duration: e?.target?.value },
                    })
                  }
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Complexité
                  </label>
                  <select
                    value={formData?.criteria?.complexity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        criteria: { ...formData?.criteria, complexity: e?.target?.value },
                      })
                    }
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                  >
                    <option value="low">Faible</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Élevée</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button type="submit" variant="default" className="flex-1" disabled={loading}>
                  {loading ? 'Création...' : 'Créer le devis'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteManagementPanel;